import { isAsyncFn } from '@/utils/function.js'

export default class Scheduler {
  #fn = null
  #interval = 0
  #isAsyncFn = false
  #firstRunTimestamp = 0
  #lastRunTimestamp = 0
  #runInBackground = false
  #isRunning = false
  #isExecutingHandler = false
  #timeoutHandler = null

  constructor(fn, interval, runInBackground) {
    this.#fn = fn
    this.#interval = interval
    this.#isAsyncFn = isAsyncFn(fn)
    this.#runInBackground = runInBackground

    window.addEventListener('blur', this.#onBlur, { passive: true })
    window.addEventListener('focus', this.#onFocus, { passive: true })

    // TODO: make use of https://developer.mozilla.org/en-US/docs/Web/API/BroadcastChannel to sync tabs and prevent duplicate requests
  }

  deconstructor = () => {
    this.stop()
    window.removeEventListener('blur', this.#onBlur)
    window.removeEventListener('focus', this.#onFocus)
  }

  get isRunning() {
    return this.#isRunning
  }

  get firstRunTimestamp() {
    return this.#firstRunTimestamp
  }

  get lastRunTimestamp() {
    return this.#lastRunTimestamp
  }

  get isExecutingHandler() {
    return this.#isExecutingHandler
  }

  start = async runImmediately => {
    if (this.#isRunning) return

    this.#isRunning = true

    if (runImmediately) await this.#handler(true)

    this.#scheduleNextTick()
  }

  stop = () => {
    clearTimeout(this.#timeoutHandler)
    this.#timeoutHandler = null
    this.#isRunning = false
  }

  reset = () => {
    this.stop()
    this.#firstRunTimestamp = 0
    this.#lastRunTimestamp = 0
  }

  updateInterval = (interval, applyImmediately) => {
    this.#interval = interval

    if (applyImmediately) {
      this.stop()
      this.start(true).then()
    }
  }

  #scheduleNextTick = () => {
    if (!this.#isRunning) return

    // use timeout instead of interval to ensure that invoked async fn finishes execution before waiting for next interval
    this.#timeoutHandler = setTimeout(this.#handler, this.#interval)
  }

  #handler = async (force = false) => {
    if (
      this.#isExecutingHandler ||
      !this.#isRunning ||
      (!this.#runInBackground && !document.hasFocus() && !force)
    )
      return

    this.#isExecutingHandler = true

    if (this.#firstRunTimestamp === 0) this.#firstRunTimestamp = Date.now()
    this.#lastRunTimestamp = Date.now()

    try {
      if (this.#isAsyncFn) await this.#fn()
      else this.#fn()
    } catch (e) {
      console.error('[Scheduler] Invoked method error', e)
    } finally {
      this.#isExecutingHandler = false
      this.#scheduleNextTick()
    }
  }

  #onBlur = () => {
    if (!this.#isRunning || this.#runInBackground) return

    clearTimeout(this.#timeoutHandler)
    this.#timeoutHandler = null
  }

  #onFocus = async () => {
    if (!this.#isRunning || this.#isExecutingHandler) return

    if (Date.now() - this.#lastRunTimestamp >= this.#interval) {
      this.stop()
      await this.start(true)
    }
  }
}
